import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function EditSettlementDialog({ opa, onClose, onSaved }) {
  const [formNumber, setFormNumber] = useState("");
  const [totalValue, setTotalValue] = useState("");
  const [date, setDate] = useState("");
  const [txnId, setTxnId] = useState("");
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (opa) {
      const r = opa.reimbursement || {};
      const s = opa.settlement || {};
      setFormNumber(r.form_number || "");
      setTotalValue(String(r.total_value ?? ""));
      setDate(s.date || "");
      setTxnId(s.transaction_id || "");
      setRemarks(s.remarks || "");
    }
  }, [opa]);

  if (!opa) return null;

  const submit = async () => {
    const v = parseFloat(totalValue);
    if (isNaN(v) || v < 0) return toast.error("Total Value must be a non-negative number");
    if (!formNumber.trim()) return toast.error("Form Number is required");
    if (!date) return toast.error("Settlement date is required");
    setSaving(true);
    try {
      const { data } = await api.patch(`/opa-requests/${opa.id}/settlement-edit`, {
        form_number: formNumber.trim(),
        total_value: v,
        date,
        transaction_id: txnId.trim() || null,
        remarks: remarks.trim() || null,
      });
      toast.success(`Updated ${opa.code}`);
      onSaved && onSaved(data);
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!opa} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-zinc-950 border-white/10 text-zinc-100 sm:max-w-lg"
        data-testid="edit-settlement-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Pencil className="w-5 h-5 text-zinc-300" /> Rectify Settlement
          </DialogTitle>
          <DialogDescription className="text-zinc-500">
            Update the reimbursement and settlement details for{" "}
            <span className="font-mono text-emerald-400">{opa.code}</span>.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Reimbursement Form Number</Label>
            <Input value={formNumber} onChange={(e) => setFormNumber(e.target.value)} className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid="edit-form-number" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Total Value (₹)</Label>
            <Input type="number" min="0" step="0.01" value={totalValue} onChange={(e) => setTotalValue(e.target.value)} className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid="edit-total-value" />
            <p className="text-[11px] text-zinc-500 mt-1">Delta and direction recompute automatically.</p>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Settlement Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid="edit-date" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Transaction ID / UTR</Label>
            <Input value={txnId} onChange={(e) => setTxnId(e.target.value)} placeholder="e.g., UTR2026062000123" className="mt-1.5 bg-zinc-900 border-white/10 text-white" data-testid="edit-txn-id" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs text-zinc-400">Remarks</Label>
            <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5 bg-zinc-900 border-white/10 text-white min-h-[70px]" data-testid="edit-remarks" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-zinc-400 hover:text-white" data-testid="edit-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-white text-black hover:bg-zinc-200 font-medium" data-testid="edit-submit">
            {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
